import Spinner from '@components/_elements/Spinner/Spinner';
import { isActionRole } from '@src/services/auth';
import { UserRole } from '@src/services/auth/roles';
import { SiteMeta } from '@src/types/SiteMeta';
import { WidgetTypeDeviceList } from '@src/types/Widget';
import storeConnector from '@store/storeConnector';
import React, { useMemo, useState } from 'react';
import PowinControllerCommandsModal from '@components/Battery/Powin/Commands/PowinController/PowinControllerCommandsModal';
import useSubscription from '@hooks/useSubscription';
import DeviceDataTable from '@components/_shared/DeviceDataTable';
import { useCurrentPowinControllerIdContext } from '@hooks/useCurrentPowinControllerId';
import UIBox from '@components/_shared/UIBox';
import useGetWidgetConfig from '@src/hooks/api/queries/useGetWidgetConfig';

interface ListContentProps {
  siteMeta: SiteMeta;
  role: UserRole;
  openCmdModal: (id: string) => void;
}

function ListContent({ siteMeta, openCmdModal, role }: ListContentProps) {
  const { currentPowinControllerId, setCurrentPowinControllerId } =
    useCurrentPowinControllerIdContext();
  const deviceNameDictionary = useMemo(
    () =>
      siteMeta.PowinControllers.map((powinControllerId, i) => ({
        id: powinControllerId,
        name: siteMeta.PowinControllerNames[i],
      })),
    [siteMeta],
  );

  const { data: widgetConfig, isLoading } =
    useGetWidgetConfig<WidgetTypeDeviceList>('powinControllerList');

  const dataPointsWithValues = useSubscription(widgetConfig?.dataPoints || []);
  return isLoading ? (
    <Spinner type='content-small' />
  ) : (
    <DeviceDataTable
      enableCmdBtnColumn={
        (siteMeta.ui.Command_Access_Enable ||
          !process.env.VITE_READ_ONLY_MODE) &&
        isActionRole(role)
      }
      hideFooter
      data={dataPointsWithValues}
      openCmdModal={openCmdModal}
      rowSelectionModel={[currentPowinControllerId]}
      onRowSelectionModelChange={([newTscId]: string) => {
        if (newTscId) setCurrentPowinControllerId(newTscId);
      }}
      deviceNameDictionary={deviceNameDictionary}
      getRowClassName={() => 'MuiDataGrid-clickable'}
    />
  );
}

function PowinControllerList({
  siteMeta,
  role,
}: {
  siteMeta: SiteMeta;
  role: UserRole;
}) {
  const [cmdModal, setCmdModal] = useState(false);
  const [deviceIdForCmdModal, setDeviceIdForCmdModal] = useState<string>('');

  const powinControllerNameForCmdModal = useMemo(
    () =>
      siteMeta.PowinControllerNames[
        siteMeta.PowinControllers.indexOf(deviceIdForCmdModal)
      ],
    [siteMeta, deviceIdForCmdModal],
  );

  const closeCmdModal = () => {
    setCmdModal(false);
  };

  return (
    <UIBox header='POWIN EMS List'>
      <ListContent
        role={role}
        siteMeta={siteMeta}
        openCmdModal={(deviceId: string) => {
          setDeviceIdForCmdModal(deviceId);
          setCmdModal(true);
        }}
      />
      <PowinControllerCommandsModal
        deviceName={powinControllerNameForCmdModal}
        deviceId={deviceIdForCmdModal}
        open={cmdModal}
        onClose={() => closeCmdModal()}
      />
    </UIBox>
  );
}

export default storeConnector(PowinControllerList, {
  config: ['siteMeta'],
  user: ['role'],
});
